@if (orgChart) {
  <div #orgChartContainer class="col-span-2 border-t pt-7 border-gray-300">
    <div class="text-2xl font-bold text-gray-800 mb-8">Organization Chart</div>

    <div class="flex flex-col items-center">
      <!-- Manager Level -->
      @if (orgChart.manager) {
        <div class="flex flex-col items-center">
          <div
            #managerCard
            class="bg-white border-2 border-blue-500 rounded-lg shadow-md p-4 w-full max-w-xs hover:shadow-lg transition-shadow cursor-pointer"
            [routerLink]="['/employee', orgChart.manager.id]"
          >
            <div class="flex items-center space-x-3">
              <div
                class="w-12 h-12 shrink-0 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg"
              >
                {{ orgChart.manager.firstName?.charAt(0)
                }}{{ orgChart.manager.lastName?.charAt(0) }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="font-semibold text-gray-800 truncate">
                  {{ orgChart.manager.firstName }}
                  {{ orgChart.manager.lastName }}
                </div>
                <div class="text-sm text-gray-500">Manager</div>
              </div>
            </div>
          </div>

          <!-- Vertical connector -->
          <div class="w-0.5 h-8 bg-gray-300"></div>
        </div>
      }

      <!-- Current Employee Level -->
      <div class="flex flex-col items-center w-full">
        <div
          #employeeCard
          class="bg-blue-500 rounded-lg shadow-lg p-4 sm:w-3xs max-w-sm text-white"
        >
          <div class="flex items-center space-x-3">
            <div
              class="w-14 h-14 shrink-0 bg-white rounded-full flex items-center justify-center text-blue-600 font-bold text-xl"
            >
              {{ orgChart.employee.firstName?.charAt(0)
              }}{{ orgChart.employee.lastName?.charAt(0) }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-bold text-lg truncate">
                {{ orgChart.employee.firstName }}
                {{ orgChart.employee.lastName }}
              </div>
              <!-- <div class="text-sm text-blue-100">
                {{ orgChart.employee?.status }}
              </div> -->
              <div class="text-xs mt-1">
                <span
                  class="px-2 py-0.5 rounded-full text-xs inline-block"
                  [ngClass]="{
                    'bg-green-100 text-green-700':
                      orgChart.employee.status === 'ACTIVE',
                    'bg-red-100 text-red-700':
                      orgChart.employee.status === 'INACTIVE',
                  }"
                >
                  {{ orgChart.employee.status }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Connector to subordinates -->
        @if (orgChart.subordinates.length > 0) {
          <div class="w-0.5 h-8 bg-gray-300"></div>
        }
      </div>

      <!-- Subordinates Level -->
      @if (orgChart.subordinates.length > 0) {
        <div class="w-full max-w-6xl">
          <!-- Subordinates container with connecting lines -->
          <div class="relative">
            <!-- Top horizontal connector line (only visible on larger screens with multiple subordinates) -->
            <!-- @if (orgChart.subordinates.length > 1) {
              <div class="hidden lg:block absolute top-0 left-0 right-0 h-px">
                <div class="flex justify-center items-start h-8">
                  <div
                    class="h-px bg-gray-300"
                    [style.width]="
                      'calc(' +
                      (Math.min(orgChart.subordinates.length, 4) * 100) / 4 +
                      '% - 2rem)'
                    "
                  ></div>
                </div>
              </div>
            } -->

            <div class="border border-gray-300 rounded-lg shadow-md">
              <!-- Subordinates grid -->
              <div
                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6"
              >
                @for (sub of orgChart.subordinates; track sub.id) {
                  <div class="flex flex-col items-center">
                    <!-- Vertical connector (hidden on mobile) -->
                    <!-- <div
                    class="hidden lg:block w-0.5 h-8 bg-gray-300 -mt-6"
                  ></div> -->

                    <div
                      #subCard
                      class="bg-white border-2 border-gray-200 rounded-lg shadow-md p-3 w-full hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer"
                      [routerLink]="['/employee', sub.id]"
                    >
                      <div class="flex items-center space-x-3">
                        <div
                          class="w-12 h-12 shrink-0 bg-linear-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center text-white font-bold"
                        >
                          {{ sub.firstName?.charAt(0)
                          }}{{ sub.lastName?.charAt(0) }}
                        </div>
                        <div class="flex-1 min-w-0">
                          <div
                            class="font-semibold text-gray-800 text-sm truncate"
                          >
                            {{ sub.firstName }} {{ sub.lastName }}
                          </div>
                          <div class="text-xs text-gray-500">
                            ID: {{ sub.id }}
                          </div>
                          <div class="text-xs mt-1">
                            <span
                              class="px-2 py-0.5 rounded-full text-xs inline-block"
                              [ngClass]="{
                                'bg-green-100 text-green-700':
                                  sub.status === 'ACTIVE',
                                'bg-red-100 text-red-700':
                                  sub.status === 'INACTIVE',
                              }"
                            >
                              {{ sub.status }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                }
              </div>

              <!-- Load more button -->
              @if (orgChart.hasMoreSubordinates) {
                <div class="flex justify-center mb-6">
                  <button
                    class="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-md flex items-center space-x-2"
                    (click)="loadMore.emit()"
                    [disabled]="loading"
                  >
                    @if (loading) {
                      <svg
                        class="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        ></circle>
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Loading...</span>
                    } @else {
                      <svg
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                      <span>Load More Subordinates</span>
                    }
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  </div>
}
